import L from 'leaflet'

const POI_ADDRESS_CACHE_KEY = 'poi_address_cache_v1'
const POI_ADDRESS_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000 // 7 days
let activePoisLoad = null
const activePoiAddressLoads = new Map()

function normalizePolygonGeoJson(geojson) {
    if (!geojson) return null
    if (typeof geojson !== 'string') return geojson

    try {
        return JSON.parse(geojson)
    } catch (e) {
        return null
    }
}

function isValidLatLng(lat, lng) {
    return (
        Number.isFinite(lat) &&
        Number.isFinite(lng) &&
        lat >= -90 &&
        lat <= 90 &&
        lng >= -180 &&
        lng <= 180
    )
}

function getPoiDisplayLatLng(poi) {
    const lat = Number(poi.lat)
    const lng = Number(poi.lng)
    const hasCoordinates =
        poi.lat !== null &&
        poi.lat !== undefined &&
        poi.lat !== '' &&
        poi.lng !== null &&
        poi.lng !== undefined &&
        poi.lng !== ''

    if (hasCoordinates && isValidLatLng(lat, lng)) {
        return [lat, lng]
    }

    if (poi.area_type === 'polygon' && poi.polygon_geojson) {
        try {
            const layer = L.geoJSON(poi.polygon_geojson)
            const bounds = layer.getBounds()

            if (bounds.isValid()) {
                const center = bounds.getCenter()
                return isValidLatLng(center.lat, center.lng) ? [center.lat, center.lng] : null
            }
        } catch (e) {
            return null
        }
    }

    return null
}

function getAddressCache() {
    try {
        const raw = sessionStorage.getItem(POI_ADDRESS_CACHE_KEY)
        return raw ? JSON.parse(raw) : {}
    } catch (e) {
        return {}
    }
}

function setAddressCache(cache) {
    try {
        sessionStorage.setItem(POI_ADDRESS_CACHE_KEY, JSON.stringify(cache))
    } catch (e) {
        // Ignore storage errors.
    }
}

function setAddressCacheEntry(cacheKey, address, cachedAt) {
    const cache = getAddressCache()
    cache[cacheKey] = { address, cachedAt }
    setAddressCache(cache)
}

function getAddressCacheKey(poi, vueInstance) {
    if (poi.lat == null || poi.lng == null) return null

    const lat = Number(poi.lat).toFixed(5)
    const lng = Number(poi.lng).toFixed(5)
    const locale = vueInstance.$locale ? vueInstance.$locale() : 'it'
    const composition =
        vueInstance?.$store?.state?.config?.user?.addressComposition?.join('|') || 'default'

    return `${lat},${lng}:${locale}:${composition}`
}

function isCacheEntryValid(entry) {
    return !!entry && !!entry.address && Date.now() - entry.cachedAt < POI_ADDRESS_CACHE_TTL_MS
}

function normalizePoiAddressFromCache(poi, cache, vueInstance) {
    const cacheKey = getAddressCacheKey(poi, vueInstance)
    if (!cacheKey) {
        return { poi, cacheKey: null, cached: null }
    }

    const cached = cache[cacheKey]

    if (isCacheEntryValid(cached)) {
        poi.address = cached.address
        poi.address_cached_at = cached.cachedAt
    }

    return { poi, cacheKey, cached }
}

const actions = {
    async loadPoiCategories({ commit }, payload) {
        const vm = payload.vueInstance

        return vm
            .$fetch(vm.$env.GOLIA_API + '/poi/categories')
            .then((res) => res.json())
            .then((res) => {
                const categories = res.data || []
                commit('setPoiCategories', categories)
                return categories
            })
    },
    loadPois({ commit }, payload) {
        if (activePoisLoad) return activePoisLoad

        let vm = payload.vueInstance
        commit('setPoisLoading', true)

        const request = vm.$fetch(vm.$env.GOLIA_API + '/poi')
            .then((res) => res.json())
            .then(async (res) => {
                const responsePois = Array.isArray(res.data) ? res.data : []
                let pois = responsePois.map((p) => {
                    p.polygon_geojson = normalizePolygonGeoJson(p.polygon_geojson)
                    p.area_type = p.area_type || (p.polygon_geojson ? 'polygon' : 'circle')
                    p.displayLatLng = getPoiDisplayLatLng(p)
                    p.latLng =
                        p.lat != null && p.lng != null ? p.displayLatLng : null
                    p.visible = false
                    return p
                })

                const cache = getAddressCache()
                pois = pois.map((poi) => {
                    const normalized = normalizePoiAddressFromCache(poi, cache, vm)
                    return normalized.poi
                })

                // Only cached addresses are hydrated here. Network reverse geocoding is lazy and
                // starts when a POI row is actually rendered in PoiManagerPanel.
                commit('setPois', pois)
                commit('setPoisLoaded', true)

                return pois
            })
            .finally(() => commit('setPoisLoading', false))

        activePoisLoad = request.finally(() => {
            activePoisLoad = null
        })

        return activePoisLoad
    },
    loadPoiAddress({ commit }, payload) {
        const vm = payload.vueInstance
        const poi = payload.poi
        const cacheKey = getAddressCacheKey(poi, vm)
        if (!cacheKey) return Promise.resolve(null)
        const pendingKey = `${poi.id}:${cacheKey}`

        const cached = getAddressCache()[cacheKey]
        if (isCacheEntryValid(cached)) {
            commit('setPoiAddresses', [
                {
                    id: poi.id,
                    lat: poi.lat,
                    lng: poi.lng,
                    address: cached.address,
                    cachedAt: cached.cachedAt
                }
            ])
            return Promise.resolve(cached.address)
        }

        if (activePoiAddressLoads.has(pendingKey)) return activePoiAddressLoads.get(pendingKey)

        const request = vm
            .$reverseGeocoding({ lat: poi.lat, lng: poi.lng })
            .then((address) => {
                if (!address) return null

                const cachedAt = Date.now()
                setAddressCacheEntry(cacheKey, address, cachedAt)
                commit('setPoiAddresses', [
                    {
                        id: poi.id,
                        lat: poi.lat,
                        lng: poi.lng,
                        address,
                        cachedAt
                    }
                ])
                return address
            })
            .finally(() => activePoiAddressLoads.delete(pendingKey))

        activePoiAddressLoads.set(pendingKey, request)
        return request
    },
    async createPoi({ commit, dispatch }, payload) {
        let vm = payload.vueInstance
        let poi = payload.item

        commit('setLoading', true)

        vm.$fetch(vm.$env.GOLIA_API + '/poi', {
            method: 'POST',
            body: JSON.stringify(poi)
        })
            .then((res) => {
                return res.json()
            })
            .then((res) => {
                if (!res.errors) {
                    poi.id = res.data.id
                    poi.visible = false

                    commit('addPoi', poi)
                    vm.$snotify.success(vm.__('manage_poi.messages.add_poi_success'), {
                        position: 'centerTop'
                    })

                    dispatch('loadPoiCategories', { vueInstance: vm })
                    dispatch('loadPois', { vueInstance: vm })
                } else {
                    vm.$snotify.error(vm.__('manage_poi.messages.add_poi_error'), {
                        position: 'centerTop'
                    })
                }

                commit('setLoading', false)
            })
            .catch((err) => {
                vm.$snotify.error(err, { position: 'centerTop' })

                commit('setLoading', false)
            })
    },
    async updatePoi({ commit, dispatch }, payload) {
        let vm = payload.vueInstance
        let poi = payload.item

        let poiToUpdate = {
            color: poi.color,
            description: poi.description,
            lat: poi.lat,
            lng: poi.lng,
            note: poi.note,
            radius: poi.area_type === 'polygon' ? null : poi.radius,
            type: poi.type,
            area_type: poi.area_type || 'circle',
            polygon_geojson: poi.area_type === 'polygon' ? poi.polygon_geojson || null : null
        }

        commit('setLoading', true)

        vm.$fetch(vm.$env.GOLIA_API + '/poi/' + poi.id, {
            method: 'PUT',
            body: JSON.stringify(poiToUpdate)
        })
            .then((res) => {
                return res.json()
            })
            .then((res) => {
                if (!res.errors) {
                    poi.visible = false

                    commit('updatePoi', poi)
                    vm.$snotify.success(vm.__('manage_poi.messages.update_poi_success'), {
                        position: 'centerTop'
                    })

                    dispatch('loadPoiCategories', { vueInstance: vm })
                    dispatch('loadPois', { vueInstance: vm })
                } else {
                    vm.$snotify.error(vm.__('manage_poi.messages.update_poi_error'), {
                        position: 'centerTop'
                    })
                }

                commit('setLoading', false)
            })
            .catch((err) => {
                vm.$snotify.error(err, { position: 'centerTop' })

                commit('setLoading', false)
            })
    },
    async deletePoi({ commit, dispatch }, payload) {
        let vm = payload.vueInstance
        let poi = payload.item

        commit('setLoading', true)

        vm.$fetch(vm.$env.GOLIA_API + '/poi/' + poi.id, {
            method: 'DELETE'
        })
            .then((res) => {
                if (!res.errors) {
                    commit('removePoi', poi)
                    vm.$snotify.success(vm.__('manage_poi.messages.delete_poi_success'), {
                        position: 'centerTop'
                    })

                    dispatch('loadPois', { vueInstance: vm })
                } else {
                    vm.$snotify.error(vm.__('manage_poi.messages.delete_poi_error'), {
                        position: 'centerTop'
                    })
                }

                commit('setLoading', false)
            })
            .catch((err) => {
                vm.$snotify.error(err, { position: 'centerTop' })

                commit('setLoading', false)
            })
    },
    async loadPoisNotifications({ commit }, payload) {
        let vm = payload.vueInstance
        let url = vm.$env.GOLIA_API + '/poi/notifications'

        vm.$fetch(url)
            .then((res) => {
                return res.json()
            })
            .then((res) => {
                commit('setPoisNotifications', res.data)
            })
    },
    async createPoiNotification({ dispatch, commit }, payload) {
        let vm = payload.vueInstance
        let poiItem = payload.item
        let notificationSettings = payload.notificationSettings
        let url = vm.$env.GOLIA_API + '/poi/notifications'

        let poiNotification = {
            poiId: poiItem.id,
            emailAddresses: notificationSettings?.emailAddresses || [],
            notificationEvents: poiItem?.alert?.notification_events || []
        }

        vm.$fetch(url, {
            method: 'POST',
            body: JSON.stringify(poiNotification),
        })
            .then((res) => {
                return res.json()
            })
            .then((res) => {
                vm.$snotify.success(vm.__('manage_poi.messages.create_poi_notification_success'), {
                    position: 'centerTop'
                })

                dispatch('loadPoisNotifications', { vueInstance: vm })
                // commit('addPoiNotification', res.data)
            })
    },
    async updatePoiNotification({ dispatch }, payload) {
        let vm = payload.vueInstance
        let poiItem = payload.item
        let notificationSettings = payload.notificationSettings
        let url = vm.$env.GOLIA_API + '/poi/notifications'

        let poiNotification = {
            emailAddresses: notificationSettings?.emailAddresses || []
        }

        if (poiItem?.alert?.notification_events) {
            poiNotification.notificationEvents = poiItem.alert.notification_events || []
        }

        vm.$fetch(url + '/' + (poiItem.id || poiItem.poi_id), {
            method: 'PUT',
            body: JSON.stringify(poiNotification),
        })
            .then((res) => {
                return res.json()
            })
            .then((res) => {
                vm.$snotify.success(vm.__('manage_poi.messages.update_poi_notification_success'), {
                    position: 'centerTop'
                })

                dispatch('loadPoisNotifications', { vueInstance: vm })
            })
    },
    async deletePoiNotification({ dispatch, commit }, payload) {
        let vm = payload.vueInstance
        let poiItem = payload.item
        let url = vm.$env.GOLIA_API + '/poi/notifications'

        vm.$fetch(url + '/' + poiItem.id, {
            method: 'DELETE',
        })
            .then((res) => {
                return res.json()
            })
            .then((res) => {
                vm.$snotify.success(vm.__('manage_poi.messages.delete_poi_notification_success'), {
                    position: 'centerTop'
                })

                dispatch('loadPoisNotifications', { vueInstance: vm })
                // commit('removePoiNotification', poiItem.id)
            })
    }
}

export default actions
